// Workout detail page (route /history/:id). Shows header stats, a muscle-split
// breakdown computed client-side from the Exercise catalog (primaryMuscle
// weighted by set count), a per-exercise SET/REPS table, and an inline edit
// mode that PATCHes /v1/workouts/:id. No exercise thumbnails (locked decision
// 3 in docs/pre-deploy-fixes.md) — names + sets/reps + muscle bars only.
import type { Exercise, Set as WorkoutSet, UpdateWorkoutInput } from '@onflow/shared';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '../components/v2/EmptyState.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard, FlowLabel, FlowMetric } from '../components/v2/FlowCard.js';
import { FlowInput } from '../components/v2/FlowInput.js';
import { FlowStatus } from '../components/v2/FlowStatus.js';
import { useMe } from '../hooks/useAuth.js';
import { useExercises } from '../hooks/useExercises.js';
import { useUpdateWorkout, useWorkout } from '../hooks/useWorkouts.js';
import {
  formatDuration,
  formatWeight,
  kgToUnit,
  muscleLabel,
  unitLabel,
  unitToKg,
} from '../lib/format.js';

interface MuscleSlice {
  group: string;
  setCount: number;
  pct: number;
}

export function WorkoutDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const me = useMe();
  const exercises = useExercises();
  const workoutQuery = useWorkout(id);
  const update = useUpdateWorkout(id ?? '');

  const units = me.data?.profile.units ?? 'KG';

  const exerciseById = useMemo(() => {
    const map = new Map<string, Exercise>();
    for (const ex of exercises.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercises.data]);

  const workout = workoutQuery.data;

  // Muscle split: weight each exercise's primaryMuscle by its set count, then
  // express as a percentage of total weighted sets. Unmatched exercises (free
  // text ids from AI plan sessions with no catalog row) are skipped.
  const muscleSlices = useMemo<MuscleSlice[]>(() => {
    if (!workout) return [];
    const counts = new Map<string, number>();
    let total = 0;
    for (const ex of workout.exercises) {
      const cat = exerciseById.get(ex.exerciseId);
      if (!cat) continue;
      const weight = ex.sets.length;
      counts.set(cat.primaryMuscle, (counts.get(cat.primaryMuscle) ?? 0) + weight);
      total += weight;
    }
    if (total === 0) return [];
    return Array.from(counts.entries())
      .map(([group, setCount]) => ({ group, setCount, pct: Math.round((setCount / total) * 100) }))
      .sort((a, b) => b.setCount - a.setCount);
  }, [workout, exerciseById]);

  const [showAllMuscles, setShowAllMuscles] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSets, setDraftSets] = useState<WorkoutSet[][]>([]);

  if (workoutQuery.isLoading) {
    return (
      <section className="fade-in">
        <EmptyState title="Loading workout…" />
      </section>
    );
  }
  if (workoutQuery.error || !workout) {
    return (
      <section className="fade-in">
        <EmptyState
          tone="error"
          role="alert"
          title="Couldn't load this workout"
          action={
            <FlowButton variant="soft" onClick={() => navigate('/history')}>
              Back to history
            </FlowButton>
          }
        />
      </section>
    );
  }

  const exerciseName = (exId: string): string => exerciseById.get(exId)?.name ?? exId;
  const visibleMuscles = showAllMuscles ? muscleSlices : muscleSlices.slice(0, 3);

  const startEdit = () => {
    setDraftTitle(workout.title);
    setDraftSets(workout.exercises.map((ex) => ex.sets.map((s) => ({ ...s }))));
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const setDraftValue = (
    exIdx: number,
    setIdx: number,
    field: 'weight' | 'reps',
    value: number,
  ) => {
    setDraftSets((prev) =>
      prev.map((sets, i) =>
        i === exIdx ? sets.map((s, j) => (j === setIdx ? { ...s, [field]: value } : s)) : sets,
      ),
    );
  };

  const saveEdit = () => {
    const payload: UpdateWorkoutInput = {
      title: draftTitle.trim() || workout.title,
      exercises: workout.exercises.map((ex, i) => ({
        ...ex,
        sets: draftSets[i] ?? ex.sets,
      })),
    };
    update.mutate(payload, { onSuccess: () => setEditing(false) });
  };

  return (
    <section className="fade-in">
      <div style={{ padding: '6px 0 10px' }}>
        <FlowButton variant="ghost" size="sm" onClick={() => navigate('/history')}>
          ← History
        </FlowButton>
      </div>

      {editing ? (
        <FlowInput
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          wrapperStyle={{ marginBottom: 16 }}
        />
      ) : (
        <h1
          style={{
            margin: '0 0 16px',
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-size)',
            lineHeight: 'var(--text-title-line)',
            letterSpacing: 'var(--text-title-tracking)',
            fontWeight: 700,
            color: 'var(--ink-1)',
          }}
        >
          {workout.title}
        </h1>
      )}

      <FlowCard style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Stat label="Time" value={formatDuration(workout.duration)} />
          <Stat label="Volume" value={formatWeight(workout.totalVolume, units)} />
          <Stat label="Sets" value={String(workout.totalSets)} />
        </div>
      </FlowCard>

      {muscleSlices.length > 0 && (
        <FlowCard style={{ marginBottom: 12 }}>
          <FlowLabel style={{ marginBottom: 12 }}>Muscle split</FlowLabel>
          {visibleMuscles.map((m) => (
            <div key={m.group} style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                  marginBottom: 4,
                  color: 'var(--ink-2)',
                }}
              >
                <span>{muscleLabel(m.group)}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--ink-3)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {m.pct}%
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 'var(--r-full)',
                  background: 'var(--paper-2)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ width: `${m.pct}%`, height: '100%', background: 'var(--ember)' }} />
              </div>
            </div>
          ))}
          {muscleSlices.length > 3 && (
            <FlowButton variant="quiet" size="sm" onClick={() => setShowAllMuscles((v) => !v)}>
              {showAllMuscles ? 'Show less' : `Show ${muscleSlices.length - 3} more`}
            </FlowButton>
          )}
        </FlowCard>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {workout.exercises.map((ex, exIdx) => (
          <FlowCard key={exIdx}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: 'var(--ink-1)' }}>
              {exerciseName(ex.exerciseId)}
            </div>
            <table
              style={{
                width: '100%',
                fontSize: 13,
                color: 'var(--ink-2)',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    textAlign: 'left',
                    color: 'var(--ink-3)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  <th style={{ fontWeight: 500, paddingBottom: 6 }}>Set</th>
                  <th style={{ fontWeight: 500, paddingBottom: 6 }}>Weight ({unitLabel(units)})</th>
                  <th style={{ fontWeight: 500, paddingBottom: 6 }}>Reps</th>
                </tr>
              </thead>
              <tbody>
                {ex.sets.map((s, setIdx) => (
                  <tr key={setIdx}>
                    <td style={{ padding: '4px 0', color: 'var(--ink-3)' }}>
                      {s.setNumber || setIdx + 1}
                    </td>
                    <td style={{ padding: '4px 0' }}>
                      {editing ? (
                        <input
                          type="number"
                          value={kgToUnit(draftSets[exIdx]?.[setIdx]?.weight ?? s.weight, units)}
                          onChange={(e) =>
                            setDraftValue(
                              exIdx,
                              setIdx,
                              'weight',
                              unitToKg(Number(e.target.value), units),
                            )
                          }
                          style={editCellStyle}
                        />
                      ) : (
                        formatWeight(s.weight, units)
                      )}
                    </td>
                    <td style={{ padding: '4px 0' }}>
                      {editing ? (
                        <input
                          type="number"
                          value={draftSets[exIdx]?.[setIdx]?.reps ?? s.reps}
                          onChange={(e) =>
                            setDraftValue(exIdx, setIdx, 'reps', Number(e.target.value))
                          }
                          style={editCellStyle}
                        />
                      ) : (
                        <>
                          {s.reps}
                          {s.isPersonalRecord && (
                            <span style={{ marginLeft: 6, display: 'inline-block' }}>
                              <FlowStatus state="on-track" label="PR" dot={false} />
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FlowCard>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, marginBottom: 40 }}>
        {editing ? (
          <>
            <FlowButton variant="primary" disabled={update.isPending} onClick={saveEdit}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </FlowButton>
            <FlowButton variant="soft" disabled={update.isPending} onClick={cancelEdit}>
              Cancel
            </FlowButton>
          </>
        ) : (
          <FlowButton variant="soft" onClick={startEdit}>
            Edit workout
          </FlowButton>
        )}
      </div>
    </section>
  );
}

const editCellStyle: React.CSSProperties = {
  width: 68,
  padding: '6px 8px',
  borderRadius: 'var(--r-xs)',
  background: 'var(--paper-0)',
  color: 'var(--ink-1)',
  border: '1px solid var(--edge)',
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <FlowLabel style={{ marginBottom: 6 }}>{label}</FlowLabel>
      <FlowMetric value={value} size="sm" />
    </div>
  );
}
